import React, { forwardRef } from "react";
import BookingAnalyticsGraph from "../components/organisms/charts/BookingAnalyticsGraph";
import LeadCollectionGraph from "../components/organisms/charts/LeadCollectionGraph";
import CancelledReasonGraph from "../components/organisms/charts/CancelledReasonGraph";
import CancellationsAndRescheduledAreaChart from "../components/organisms/charts/CancellationsAndRescheduledAreaChart";
import ClickGoogleReviewGraph from "../components/organisms/charts/ClickGoogleReviewGraph";
import RatingGraph from "../components/organisms/charts/RatingGraph";
import { parseISO, format } from "date-fns";

interface AnalyticsPdfReportProps {
  hospitalId: string;
  startDate: string;
  endDate: string;
  bookingStats?: any;
  funnelStats?: any;
  cancelStats?: any;
  rcStats?: any;
  googleStats?: any;
  ratingStats?: any;
  hospName?:string;
  leadcards?: any;
}

const AnalyticsPdfReport = forwardRef<HTMLDivElement, AnalyticsPdfReportProps>(
  (
    {
      hospitalId,
      startDate,
      endDate,
      bookingStats,
      funnelStats,
      cancelStats,
      rcStats,
      googleStats,
      ratingStats,
      hospName,
      leadcards
    },
    ref
  ) => {
    // Human-readable month/year
    const periodLabel =
      format(parseISO(startDate), "MMMM yyyy") +
      (startDate === endDate
        ? ""
        : ` — ${format(parseISO(endDate), "MMMM yyyy")}`);
    const periodRange = startDate === endDate
      ? format(parseISO(startDate), "d MMMM yyyy")
      : `${format(parseISO(startDate), "d MMMM yyyy")} to ${format(parseISO(endDate), "d MMMM yyyy")}`;

    const containerStyle: React.CSSProperties = {
      width: "210mm", // A4 width
      background: "#FFF",
      color: "#333",
      fontFamily: "Arial, sans-serif",
      fontSize: "12px",
      lineHeight: "1.4",
    };

    const pageStyle: React.CSSProperties = {
      width: "210mm",
      minHeight: "297mm", // A4 height
      padding: "10mm",
      boxSizing: "border-box",
      pageBreakAfter: "always",
      display: "flex",
      flexDirection: "column",
      position: "relative",
    };

    const headerStyle: React.CSSProperties = {
      textAlign: "center",
      marginBottom: "20px",
    };

    const titleStyle: React.CSSProperties = {
      fontSize: "24px",
      fontWeight: "bold",
      margin: "0 0 10px 0",
      color: "#333",
    };

    const subtitleStyle: React.CSSProperties = {
      fontSize: "14px",
      color: "#666",
      margin: "0",
    };

    const sectionTitleStyle: React.CSSProperties = {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#333",
      marginBottom: "10px",
      marginTop: "0",
    };

    const sectionTextStyle: React.CSSProperties = {
      fontSize: "14px",
      lineHeight: "1.5",
      marginBottom: "10px",
      color: "#555",
    };

    const listStyle: React.CSSProperties = {
      marginLeft: "20px",
      marginBottom: "20px",
      fontSize: "14px",
      lineHeight: "1.5",
    };

    const chartContainerStyle: React.CSSProperties = {
      width: "100%",
      // height: "350px",
      marginTop: "10px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      // border: "1px solid #e0e0e0",
      borderRadius: "4px",
      // backgroundColor: "#fafafa",
    };

    const splitChartContainerStyle: React.CSSProperties = {
      display: "flex",
      gap: "20px",
      marginTop: "20px",
      height: "300px",
    };

    const halfChartStyle: React.CSSProperties = {
      flex: "1",
      // border: "1px solid #e0e0e0",
      borderRadius: "4px",
      // backgroundColor: "#fafafa",
      padding: "10px",
    };

    const chartTitleStyle: React.CSSProperties = {
      fontSize: "14px",
      fontWeight: "bold",
      marginBottom: "10px",
      textAlign: "center",
    };

    return (
      <div ref={ref} style={containerStyle}>
        {/* Page 1: Booking Data */}
        <div style={pageStyle}>
          <div style={headerStyle}>
            <h1 style={titleStyle}>Monthly {hospName} Analytics Report</h1>
            <p style={subtitleStyle}>Report Period: {periodRange && `(${periodRange})`}</p>
          </div>

          <h2 style={sectionTitleStyle}>1. Booking Data</h2>
          <p style={sectionTextStyle}><strong>Overview:</strong><br/>
            This section summarizes patient appointment bookings across all channels for the selected period.
          </p>
          <ul style={listStyle}>
            <li><strong>Dashboard Appointments:</strong> {bookingStats?.totals?.["Dashboard Appointments"] || 0}</li>
            <li><strong>WhatsApp Appointments:</strong> {bookingStats?.totals?.["Whatsapp Appointments"] || 0}</li>
            <li><strong>Walk-in Bookings:</strong> {bookingStats?.totals?.["Walk-in Bookings"] || 0}</li>
            <li><strong>Follow-up Bookings:</strong> {bookingStats?.totals?.["Follow-up Bookings"] || 0}</li>
          </ul>
          <p style={sectionTextStyle}><strong>Interpretation:</strong><br/>
            A balanced distribution across booking channels indicates effective patient engagement. Notable trends or spikes should be further analyzed to optimize resource allocation and improve patient experience.
          </p>
          <div style={chartContainerStyle}>
            <BookingAnalyticsGraph
              hospitalId={hospitalId}
              startDate={startDate}
              endDate={endDate}
              report={true}
              bookingStats={bookingStats}
            />
          </div>
        </div>

        {/* Page 2: Lead Collection Funnel */}
        <div style={pageStyle}>
          <h2 style={sectionTitleStyle}>2. Lead Collection Funnel</h2>
          <p style={sectionTextStyle}><strong>Overview:</strong><br/>
            Tracks the patient journey from initial interest to confirmed appointment, highlighting conversion efficiency at each stage.
          </p>
          <ul style={listStyle}>
            <li><strong>Leads Initiated:</strong> {funnelStats?.funnel?.started || 0}</li>
            <li><strong>Date Selected:</strong> {funnelStats?.funnel?.date_selected || 0}</li>
            <li><strong>Doctor Selected:</strong> {funnelStats?.funnel?.doctor_selected || 0}</li>
            <li><strong>Session Selected:</strong> {funnelStats?.funnel?.session_selected || 0}</li>
            <li><strong>Bookings Confirmed:</strong> {funnelStats?.funnel?.booking_confirmed || 0}</li>
          </ul>
          <p style={sectionTextStyle}><strong>Interpretation:</strong><br/>
            A high drop-off between stages may indicate friction points in the booking process. Addressing these can improve conversion rates and patient satisfaction.
          </p>
          <div style={chartContainerStyle}>
            <LeadCollectionGraph
              hospitalId={hospitalId}
              startDate={startDate}
              endDate={endDate}
              report={true}
              leadcards={leadcards}
              funnelStats={funnelStats}
            />
          </div>
        </div>

        {/* Page 3: Cancellations & Rescheduling */}
        <div style={pageStyle}>
          <h2 style={sectionTitleStyle}>3. Cancellations & Rescheduling</h2>
          <p style={sectionTextStyle}><strong>Overview:</strong><br/>
            Analyzes appointment cancellations and rescheduling trends, including primary reasons and temporal spikes.
          </p>
          <ul style={listStyle}>
            <li><strong>Peak Cancellation Date:</strong> {format(parseISO(endDate), "MMMM d, yyyy")}</li>
            <li><strong>Top Reason:</strong> "Wrong booking"</li>
          </ul>
          <p style={sectionTextStyle}><strong>Interpretation:</strong><br/>
            Monitoring cancellation patterns helps identify operational bottlenecks or patient concerns. Proactive measures, such as patient reminders or clearer booking instructions, can reduce future cancellations.
          </p>

          <div style={{ marginTop: "20px" }}>

            <div style={{ ...chartContainerStyle, marginBottom: "20px" }}>
              <CancelledReasonGraph
                hospitalId={hospitalId}
                startDate={startDate}
                endDate={endDate}
                report={true}
                cancelStats={cancelStats}
              />
            </div>


            <div style={{ ...chartContainerStyle }}>
              <CancellationsAndRescheduledAreaChart
                hospitalId={hospitalId}
                startDate={startDate}
                endDate={endDate}
                report={true}
                rcStats={rcStats}
              />
            </div>
          </div>
        </div>

        {/* Page 4: Ratings & Google Reviews */}
        <div style={{ ...pageStyle, pageBreakAfter: "avoid" }}>
          <h2 style={sectionTitleStyle}>4. Ratings & Google Reviews</h2>
          <p style={sectionTextStyle}><strong>Overview:</strong><br/>
            Summarizes patient feedback and online reputation based on recent ratings and Google review activity.
          </p>
          <div >
            <div>
              <h3 style={chartTitleStyle}>Google Review Clicks</h3>
              <div style={chartContainerStyle} >
                <ClickGoogleReviewGraph
                  hospitalId={hospitalId}
                  startDate={startDate}
                  endDate={endDate}
                  report={true}
                  googleStats={googleStats}
                />
              </div>
            </div>
            <div style={{marginTop: "20px"}}>
              <h3 style={chartTitleStyle}>Rating Distribution</h3>
              <div style={chartContainerStyle}>
                <RatingGraph
                  hospitalId={hospitalId}
                  startDate={startDate}
                  endDate={endDate}
                  report={true}
                  ratingStats={ratingStats}
                />
              </div>
            </div>
          </div>
          <p style={sectionTextStyle}><strong>Interpretation:</strong><br/>
            Consistently high ratings reflect strong patient satisfaction. Monitoring review trends and addressing negative feedback promptly can further enhance the hospital's reputation.
          </p>
        </div>
      </div>
    );
  }
);

AnalyticsPdfReport.displayName = "AnalyticsPdfReport";

export default AnalyticsPdfReport;